import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Plus, Users, Shield, Trash2, Key, Pencil, Phone, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full access to all features', color: 'bg-red-100 text-red-700' },
  { value: 'manager', label: 'Manager', desc: 'Manage shifts, roster & attendance', color: 'bg-rose-100 text-rose-700' },
  { value: 'onboarding', label: 'Onboarding', desc: 'Create customers & packages', color: 'bg-sky-100 text-sky-700' },
  { value: 'sales', label: 'Sales', desc: 'Onboarding + view own data only', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'accounts', label: 'Accounts', desc: 'Raise and confirm POs, Quotations', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'delivery', label: 'Delivery/Implementation', desc: 'Manage circuit delivery', color: 'bg-violet-100 text-violet-700' },
  { value: 'noc', label: 'NOC', desc: 'Technical implementation & monitoring', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'support', label: 'Support', desc: 'Read-only access', color: 'bg-amber-100 text-amber-700' },
  { value: 'hr', label: 'HR Manager', desc: 'Manage employees, attendance & payroll', color: 'bg-teal-100 text-teal-700' },
  { value: 'enterprise_sales', label: 'Enterprise Sales', desc: 'Own customers, quotes & ILL/MPLS sales orders', color: 'bg-blue-100 text-blue-700' },
  { value: 'lco_sales', label: 'LCO Partner Sales', desc: 'Own LCOs, quotes & broadband packages for approval', color: 'bg-orange-100 text-orange-700' }
];

export default function Employees() {
  const { API, user, refreshToken } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, employee: null });
  const [editRoleDialog, setEditRoleDialog] = useState({ open: false, employee: null, selectedRoles: [] });
  const [editProfileDialog, setEditProfileDialog] = useState({ open: false, employee: null, phone: '', require_2fa: false });
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    roles: ['onboarding'],
    require_2fa: false
  });

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if user has any of the allowed roles
  const hasRole = (allowedRoles) => {
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    return allowedRoles.some(role => userRoles.includes(role));
  };

  const isAdmin = hasRole(['admin']);

  useEffect(() => {
    fetchEmployees();
  }, [API]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    if (formData.require_2fa && !formData.phone) {
      toast.error('Phone number is required when 2FA is enabled');
      return;
    }
    try {
      await axios.post(`${API}/users`, formData);
      toast.success('Employee added successfully');
      setOpen(false);
      setFormData({ email: '', password: '', full_name: '', phone: '', roles: ['onboarding'], require_2fa: false });
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    }
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => {
      const newRoles = prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role];
      return { ...prev, roles: newRoles };
    });
  };

  const handleUpdateProfile = async () => {
    const identifier = editProfileDialog.employee?.user_id || editProfileDialog.employee?.email;
    if (!identifier) {
      toast.error('Cannot update user - no identifier');
      return;
    }
    if (editProfileDialog.require_2fa && !editProfileDialog.phone) {
      toast.error('Phone number is required when 2FA is enabled');
      return;
    }
    try {
      await axios.put(`${API}/users/${identifier}`, { 
        phone: editProfileDialog.phone || null,
        require_2fa: editProfileDialog.require_2fa
      });
      toast.success('Profile updated successfully');
      setEditProfileDialog({ open: false, employee: null, phone: '', require_2fa: false });
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleEditRoleToggle = (role) => {
    setEditRoleDialog(prev => {
      const newRoles = prev.selectedRoles.includes(role)
        ? prev.selectedRoles.filter(r => r !== role)
        : [...prev.selectedRoles, role];
      return { ...prev, selectedRoles: newRoles };
    });
  };

  const handleStatusToggle = async (userId, email, currentStatus) => {
    const identifier = userId || email;
    if (!identifier) {
      toast.error('Cannot update user - no identifier');
      return;
    }
    try {
      await axios.put(`${API}/users/${identifier}`, { is_active: !currentStatus });
      toast.success('Employee status updated');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to update employee status');
    }
  };

  const handleDelete = async (userId, email) => {
    const identifier = userId || email;
    if (!identifier) {
      toast.error('Cannot delete user - no identifier');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/users/${identifier}`);
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const getRoleColor = (role) => {
    const roleInfo = ROLES.find(r => r.value === role);
    return roleInfo?.color || 'bg-slate-100 text-slate-700';
  };

  // Get user roles - support both old 'role' and new 'roles' field
  const getUserRoles = (employee) => {
    if (employee.roles && employee.roles.length > 0) {
      return employee.roles;
    }
    return employee.role ? [employee.role] : [];
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    const identifier = passwordDialog.employee?.user_id || passwordDialog.employee?.email;
    if (!identifier) {
      toast.error('Cannot update password - no identifier');
      return;
    }
    try {
      await axios.put(`${API}/users/${identifier}`, { password: newPassword });
      toast.success('Password changed successfully');
      setPasswordDialog({ open: false, employee: null });
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to change password');
    }
  };

  const [myPasswordDialog, setMyPasswordDialog] = useState(false);
  const [myPasswordForm, setMyPasswordForm] = useState({ old_password: '', new_password: '', confirm_password: '' });

  const handleChangeRoles = async () => {
    const identifier = editRoleDialog.employee?.user_id || editRoleDialog.employee?.email;
    const employeeEmail = editRoleDialog.employee?.email;
    if (!identifier || editRoleDialog.selectedRoles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    try {
      await axios.put(`${API}/users/${identifier}`, { 
        roles: editRoleDialog.selectedRoles,
        role: editRoleDialog.selectedRoles[0] // Keep legacy field updated
      });
      
      // If the updated user is the currently logged-in user, refresh their token
      if (employeeEmail === user?.email) {
        const refreshResult = await refreshToken();
        if (refreshResult.success) {
          toast.success('Your roles have been updated and your session has been refreshed!', {
            duration: 5000,
          });
        } else {
          toast.success('Roles updated! Please re-login for changes to take effect.', {
            duration: 5000,
          });
        }
      } else {
        toast.success('Roles updated successfully!', {
          duration: 3000,
        });
      }
      
      setEditRoleDialog({ open: false, employee: null, selectedRoles: [] });
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to update roles');
    }
  };

  const handleChangeMyPassword = async () => {
    if (myPasswordForm.new_password !== myPasswordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (myPasswordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: myPasswordForm.old_password,
        new_password: myPasswordForm.new_password
      });
      toast.success('Password changed successfully');
      setMyPasswordDialog(false);
      setMyPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-semibold text-slate-900 tracking-tight">Users & Roles</h2>
          <p className="text-slate-600 mt-1">Manage team members and their access permissions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setMyPasswordDialog(true)}>
            <Key className="mr-2 h-4 w-4" /> Change My Password
          </Button>
          {isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-employee-button">
                  <Plus className="mr-2 h-4 w-4" /> Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg" data-testid="employee-dialog">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-semibold text-slate-900">New User</DialogTitle>
                  <DialogDescription>Add a new team member with their access roles</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="employee-form">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      placeholder="John Doe"
                      data-testid="employee-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="john@antbroadband.com"
                      data-testid="employee-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      placeholder="••••••••"
                      data-testid="employee-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Roles * (select multiple)</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {ROLES.map(role => (
                        <div key={role.value} className="flex items-center space-x-2 p-2 border rounded-lg hover:bg-slate-50">
                          <Checkbox
                            data-testid={`role-checkbox-${role.value}`}
                            id={`role-${role.value}`}
                            checked={formData.roles.includes(role.value)}
                            onCheckedChange={() => handleRoleToggle(role.value)}
                          />
                          <label htmlFor={`role-${role.value}`} className="flex-1 cursor-pointer">
                            <span className="text-sm font-medium">{role.label}</span>
                            <p className="text-xs text-slate-500">{role.desc}</p>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number (for WhatsApp OTP)</Label>
                    <div className="flex gap-2">
                      <div className="flex items-center px-3 bg-slate-100 border rounded-md text-slate-600 text-sm">
                        +91
                      </div>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        placeholder="9876543210"
                        className="flex-1"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Required for WhatsApp OTP login</p>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-slate-50">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                      <div>
                        <Label className="text-sm font-medium">Require 2FA</Label>
                        <p className="text-xs text-slate-500">OTP verification after password login</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.require_2fa}
                      onCheckedChange={(checked) => setFormData({ ...formData, require_2fa: checked })}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="submit-employee-button">
                    Add User
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Role Legend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Role Permissions</CardTitle>
          <CardDescription>Users can have multiple roles assigned</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ROLES.map(role => (
              <div key={role.value} className="p-3 border rounded-lg">
                <Badge className={role.color}>{role.label}</Badge>
                <p className="text-xs text-slate-500 mt-1">{role.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="employees-table-card">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle className="text-xl font-semibold text-slate-900">Team Members</CardTitle>
          <CardDescription>{employees.length} registered users</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-700">Name</TableHead>
                <TableHead className="font-semibold text-slate-700">Email / Phone</TableHead>
                <TableHead className="font-semibold text-slate-700">Roles</TableHead>
                <TableHead className="font-semibold text-slate-700">Status</TableHead>
                {isAdmin && <TableHead className="font-semibold text-slate-700">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-slate-500 py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-slate-500 py-8">
                    <Users className="mx-auto h-12 w-12 text-slate-300 mb-2" />
                    <p>No users yet. Add your first team member to get started.</p>
                  </TableCell>
                </TableRow>
              ) : (
                employees.map((employee, index) => (
                  <TableRow key={employee.user_id || employee.email || index} className="hover:bg-slate-50/50" data-testid={`employee-row-${employee.email}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{employee.full_name}</span>
                        {employee.require_2fa && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                            <ShieldCheck className="w-3 h-3 mr-1" /> 2FA
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-slate-600 text-sm">
                        {employee.email}
                        {employee.phone && (
                          <div className="flex items-center gap-1 text-xs text-slate-400 mt-0.5">
                            <Phone className="w-3 h-3" /> +91 {employee.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {getUserRoles(employee).map(role => (
                          <Badge key={role} className={getRoleColor(role)}>
                            <Shield className="w-3 h-3 mr-1" />
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        employee.is_active 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {employee.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStatusToggle(employee.user_id, employee.email, employee.is_active)}
                            className="text-xs"
                            data-testid={`toggle-status-button-${employee.email}`}
                          >
                            {employee.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditRoleDialog({ 
                              open: true, 
                              employee, 
                              selectedRoles: getUserRoles(employee)
                            })}
                            className="text-xs"
                          >
                            <Pencil className="h-3 w-3 mr-1" /> Roles
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditProfileDialog({ 
                              open: true, 
                              employee, 
                              phone: employee.phone || '',
                              require_2fa: employee.require_2fa || false
                            })}
                            className="text-xs"
                          >
                            <Phone className="h-3 w-3 mr-1" /> Profile
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPasswordDialog({ open: true, employee })}
                            className="text-xs"
                          >
                            <Key className="h-3 w-3 mr-1" /> Password
                          </Button>
                          {employee.email !== user?.email && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(employee.user_id, employee.email)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialog.open} onOpenChange={(open) => setPasswordDialog({ open, employee: passwordDialog.employee })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Set a new password for {passwordDialog.employee?.full_name || passwordDialog.employee?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 6 characters)"
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPasswordDialog({ open: false, employee: null }); setNewPassword(''); }}>
              Cancel
            </Button>
            <Button onClick={handleChangePassword} className="bg-slate-900 hover:bg-slate-800">
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change My Password Dialog */}
      <Dialog open={myPasswordDialog} onOpenChange={setMyPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change My Password</DialogTitle>
            <DialogDescription>
              Update your account password
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={myPasswordForm.old_password}
                onChange={(e) => setMyPasswordForm({ ...myPasswordForm, old_password: e.target.value })}
                placeholder="Enter current password"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="my-new-password">New Password</Label>
              <Input
                id="my-new-password"
                type="password"
                value={myPasswordForm.new_password}
                onChange={(e) => setMyPasswordForm({ ...myPasswordForm, new_password: e.target.value })}
                placeholder="Enter new password (min 6 characters)"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="confirm-new-password">Confirm New Password</Label>
              <Input
                id="confirm-new-password"
                type="password"
                value={myPasswordForm.confirm_password}
                onChange={(e) => setMyPasswordForm({ ...myPasswordForm, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMyPasswordDialog(false); setMyPasswordForm({ old_password: '', new_password: '', confirm_password: '' }); }}>
              Cancel
            </Button>
            <Button onClick={handleChangeMyPassword} className="bg-slate-900 hover:bg-slate-800">
              Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Roles Dialog */}
      <Dialog open={editRoleDialog.open} onOpenChange={(open) => setEditRoleDialog({ ...editRoleDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change User Roles</DialogTitle>
            <DialogDescription>
              Update roles for {editRoleDialog.employee?.full_name || editRoleDialog.employee?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Roles (multiple allowed)</Label>
            <div className="grid grid-cols-1 gap-2 mt-3">
              {ROLES.map(role => (
                <div key={role.value} className="flex items-center space-x-2 p-2 border rounded-lg hover:bg-slate-50">
                  <Checkbox
                    data-testid={`edit-role-checkbox-${role.value}`}
                    id={`edit-role-${role.value}`}
                    checked={editRoleDialog.selectedRoles.includes(role.value)}
                    onCheckedChange={() => handleEditRoleToggle(role.value)}
                  />
                  <label htmlFor={`edit-role-${role.value}`} className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Badge className={role.color}>{role.label}</Badge>
                      <span className="text-xs text-slate-500">{role.desc}</span>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRoleDialog({ open: false, employee: null, selectedRoles: [] })}>
              Cancel
            </Button>
            <Button onClick={handleChangeRoles} className="bg-slate-900 hover:bg-slate-800">
              Update Roles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog (Phone & 2FA) */}
      <Dialog open={editProfileDialog.open} onOpenChange={(open) => setEditProfileDialog({ ...editProfileDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
            <DialogDescription>
              Update phone and 2FA settings for {editProfileDialog.employee?.full_name || editProfileDialog.employee?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Phone Number (WhatsApp)</Label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 bg-slate-100 border rounded-md text-slate-600 text-sm">
                  +91
                </div>
                <Input
                  type="tel"
                  value={editProfileDialog.phone}
                  onChange={(e) => setEditProfileDialog({ 
                    ...editProfileDialog, 
                    phone: e.target.value.replace(/\D/g, '').slice(0, 10) 
                  })}
                  placeholder="9876543210"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500">Used for WhatsApp OTP login and 2FA verification</p>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg bg-slate-50">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                <div>
                  <Label className="text-sm font-medium">Require 2FA</Label>
                  <p className="text-xs text-slate-500">OTP verification after password login</p>
                </div>
              </div>
              <Switch
                checked={editProfileDialog.require_2fa}
                onCheckedChange={(checked) => setEditProfileDialog({ ...editProfileDialog, require_2fa: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditProfileDialog({ open: false, employee: null, phone: '', require_2fa: false })}>
              Cancel
            </Button>
            <Button onClick={handleUpdateProfile} className="bg-slate-900 hover:bg-slate-800">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
